import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { runsListRuns } from "../gen";
import type { RunResponse } from "../gen/models";
import { useOrg } from "../hooks/useOrg";
import { useSearchQuery } from "../hooks/useSearchQuery";
import { useFilterParam } from "../hooks/useFilterParam";
import { useAgentOptions } from "../hooks/useAgentOptions";
import { RunDetail } from "./RunDetail";
import { PaginationControls } from "../components/common/PaginationControls";
import { ScrollableColumnContainer } from "../components/common/ScrollableColumnContainer";
import type { FilterSelectOption } from "../components/common/FilterSelect";
import {
  RunListItem,
  RunsFilterBar,
  RunDetailPlaceholder,
  RunsListEmptyState,
} from "../components/run";

const RUN_STATUS_OPTIONS: FilterSelectOption[] = [
  { value: "all", label: "All Runs" },
  { value: "prod_only", label: "Production Only" },
  { value: "completed", label: "Completed" },
  { value: "running", label: "Running" },
  { value: "failed", label: "Failed" },
];

export function RunsWorkspace() {
  const { run_id } = useParams<{ run_id?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeOrgId } = useOrg();
  const [runs, setRuns] = useState<RunResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [agentFilter, setAgentFilter] = useFilterParam("agent", "all", ["agent_type"]);
  const [statusFilter, setStatusFilter] = useFilterParam("status", "all");
  const [searchQuery, setSearchQuery] = useSearchQuery("q");

  const agentOptions = useAgentOptions();

  const loadRuns = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
        include_test: statusFilter !== "prod_only",
      };
      if (statusFilter === "prod_only") {
        params.prod_only = true;
      }
      if (agentFilter !== "all") params.agent_type = agentFilter;
      if (statusFilter !== "all" && statusFilter !== "prod_only") params.status = statusFilter;
      if (searchQuery.trim()) params.q = searchQuery.trim();

      const res = await runsListRuns(params);
      const data = (res as any).data;
      if (data && Array.isArray(data.items)) {
        setRuns(data.items);
        setTotal(data.total ?? data.items.length);
      } else if (Array.isArray(data)) {
        setRuns(data);
        setTotal(data.length);
      } else {
        setRuns([]);
        setTotal(0);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load execution runs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [agentFilter, statusFilter, searchQuery]);

  useEffect(() => {
    loadRuns();
  }, [agentFilter, statusFilter, searchQuery, page, pageSize, activeOrgId]);

  const hasSelectedItem = Boolean(run_id && runs.some((r) => r.id === run_id));

  return (
    <div className="flex h-full w-full overflow-hidden min-h-0">
      {/* ============================================================== */}
      {/* COLUMN 2: Execution Runs List Pane                             */}
      {/* ============================================================== */}
      <div
        className={`${
          run_id ? "hidden md:flex" : "flex"
        } w-full md:w-80 lg:w-[380px] xl:w-[420px] shrink-0 h-full border-r border-stone-200 dark:border-stone-800 bg-white/70 dark:bg-stone-900/60 flex-col min-h-0 overflow-hidden select-none transition-all`}
      >
        {/* Pane Header */}
        <div className="p-3.5 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between gap-2 shrink-0 bg-stone-50/50 dark:bg-stone-950/50">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-sm font-bold tracking-tight text-stone-900 dark:text-stone-100 truncate">
              Execution Runs
            </h2>
            <span className="px-1.5 py-0.2 rounded-md bg-stone-200 dark:bg-stone-800 text-[10px] font-mono font-semibold text-stone-600 dark:text-stone-400">
              {total > 0 ? total : runs.length}
            </span>
          </div>

          <button
            type="button"
            onClick={loadRuns}
            disabled={loading}
            className="p-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 transition"
            title="Refresh runs list"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {/* Search & Filters Bar */}
        <RunsFilterBar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          agentFilter={agentFilter}
          onAgentChange={setAgentFilter}
          statusFilter={statusFilter}
          onStatusChange={setStatusFilter}
          agentOptions={agentOptions}
          statusOptions={RUN_STATUS_OPTIONS}
        />

        {/* Scrollable Runs List */}
        <ScrollableColumnContainer>
          {loading && runs.length === 0 ? (
            <div className="p-8 text-center text-stone-400 dark:text-stone-500 text-xs flex items-center justify-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin text-stone-500" />
              <span>Loading runs...</span>
            </div>
          ) : error ? (
            <div className="p-4 rounded-lg bg-rose-50 dark:bg-rose-950/60 border border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-400 text-xs">
              {error}
            </div>
          ) : runs.length === 0 ? (
            <RunsListEmptyState />
          ) : (
            runs.map((r) => (
              <RunListItem
                key={r.id}
                run={r}
                isSelected={r.id === run_id}
                isDimmed={hasSelectedItem && r.id !== run_id}
                onClick={() => navigate(`/runs/${r.id}${location.search}`)}
              />
            ))
          )}
        </ScrollableColumnContainer>

        {/* Pagination Controls */}
        <PaginationControls
          compact
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setPage(1);
          }}
          disabled={loading}
        />
      </div>

      {/* ============================================================== */}
      {/* COLUMN 3: Detail Workspace Pane                                */}
      {/* ============================================================== */}
      <div
        className={`${
          run_id ? "flex" : "hidden md:flex"
        } flex-1 min-w-0 h-full overflow-y-auto [scrollbar-gutter:stable] flex-col bg-stone-50 dark:bg-stone-950 p-4 md:p-6`}
      >
        {run_id ? (
          <>
            <div className="md:hidden pb-3 mb-2 border-b border-stone-200 dark:border-stone-800">
              <button
                type="button"
                onClick={() => navigate(`/runs${location.search}`)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-white"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Back to execution runs</span>
              </button>
            </div>
            <RunDetail key={run_id} />
          </>
        ) : (
          <RunDetailPlaceholder />
        )}
      </div>
    </div>
  );
}
